import { apiGet, APIURL, appendFlatpickrBaseStyles, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, initFlatpickrInShadow, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';
import flatpickr from 'flatpickr';

export class TraderverseMarketDividendYieldHeatmap {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private currentInterval: string = '1w';
    // Filters object to hold each filter as a separate item
    private filters = {
        p_yield_brackets: '2,4',
        p_frequency: 'Monthly',
        p_start_date: '2025-01-01',
        p_end_date: '2025-12-31',
    };
    private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;
    private widgetIndex: number = 0;

    private selectedSymbols: string[] = ['AAPL', 'AMZN'];

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        const today = new Date();
        const endDate = today.toISOString().slice(0, 10);
        const startDateObj = new Date(today);
        startDateObj.setMonth(today.getMonth() - 1);
        if (startDateObj.getMonth() === today.getMonth()) {
            startDateObj.setDate(0);
        }
        const startDate = startDateObj.toISOString().slice(0, 10);
        return {
      headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            p_min_yield: 4,
            p_top_n: 10,
            p_sort_by: 'yield',
            p_exclude_special_dividends: true,
            start_date: startDate,
            end_date: endDate,
            isSymbolSelectable: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.attachStyles();
        this.currentData = null;
        this.updateDisplay();
        this.emit('ready', {});
        this.config.onReady?.();
        this.setupChart();
        cancelSelectEvent(this.shadowRoot);
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';
        
        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const widgetHtml = `
            <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-testing-id="dividend-yield-heatmap-widget-${this.widgetIndex}" data-theme="${dataTheme}">
                <div class="headTop">
                        <h4 class="title market">${this.getTitleIconSvg()} Dividend Yield Heatmap </h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div></div>
                </div>
                <div class="inBody">
                    <div class="optionsRow">
                    <div class="item dateRange">
                            <input type="text" class="date-input" data-testing-id="date-range-input" />
                            <i><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M9.91667 1.16667H8.75V0.583333C8.75 0.428624 8.68854 0.280251 8.57915 0.170854C8.46975 0.0614581 8.32138 0 8.16667 0C8.01196 0 7.86358 0.0614581 7.75419 0.170854C7.64479 0.280251 7.58333 0.428624 7.58333 0.583333V1.16667H4.08333V0.583333C4.08333 0.428624 4.02188 0.280251 3.91248 0.170854C3.80308 0.0614581 3.65471 0 3.5 0C3.34529 0 3.19692 0.0614581 3.08752 0.170854C2.97813 0.280251 2.91667 0.428624 2.91667 0.583333V1.16667H1.75C1.28587 1.16667 0.840752 1.35104 0.512563 1.67923C0.184375 2.00742 0 2.45254 0 2.91667V9.91667C0 10.3808 0.184375 10.8259 0.512563 11.1541C0.840752 11.4823 1.28587 11.6667 1.75 11.6667H9.91667C10.3808 11.6667 10.8259 11.4823 11.1541 11.1541C11.4823 10.8259 11.6667 10.3808 11.6667 9.91667V2.91667C11.6667 2.45254 11.4823 2.00742 11.1541 1.67923C10.8259 1.35104 10.3808 1.16667 9.91667 1.16667ZM10.5 9.91667C10.5 10.0714 10.4385 10.2198 10.3291 10.3291C10.2198 10.4385 10.0714 10.5 9.91667 10.5H1.75C1.59529 10.5 1.44692 10.4385 1.33752 10.3291C1.22812 10.2198 1.16667 10.0714 1.16667 9.91667V5.83333H10.5V9.91667ZM10.5 4.66667H1.16667V2.91667C1.16667 2.76196 1.22812 2.61358 1.33752 2.50419C1.44692 2.39479 1.59529 2.33333 1.75 2.33333H2.91667V2.91667C2.91667 3.07138 2.97813 3.21975 3.08752 3.32915C3.19692 3.43854 3.34529 3.5 3.5 3.5C3.65471 3.5 3.80308 3.43854 3.91248 3.32915C4.02188 3.21975 4.08333 3.07138 4.08333 2.91667V2.33333H7.58333V2.91667C7.58333 3.07138 7.64479 3.21975 7.75419 3.32915C7.86358 3.43854 8.01196 3.5 8.16667 3.5C8.32138 3.5 8.46975 3.43854 8.57915 3.32915C8.68854 3.21975 8.75 3.07138 8.75 2.91667V2.33333H9.91667C10.0714 2.33333 10.2198 2.39479 10.3291 2.50419C10.4385 2.61358 10.5 2.76196 10.5 2.91667V4.66667Z" fill="#9F9FA2"/> </svg> </i>
                        </div>
                        
                        <span></span>
              <div class="filtersBox sideBar hidden">
                        <div class="item">
                            <label>Frequency</label>
                            <select class="frequency-select" data-testing-id="frequency-select">
                                <option value="Monthly" ${this.filters.p_frequency === 'Monthly' ? 'selected' : ''}>Monthly</option>
                                <option value="Weekly" ${this.filters.p_frequency === 'Weekly' ? 'selected' : ''}>Weekly</option>
                                <option value="Quarterly" ${this.filters.p_frequency === 'Quarterly' ? 'selected' : ''}>Quarterly</option>
                                <option value="Yearly" ${this.filters.p_frequency === 'Yearly' ? 'selected' : ''}>Yearly</option>
                            </select>
                        </div>
                        <span></span>
                        <div class="item">
                            <label>Yield Brackets</label>
                            <input type="text" class="yield-brackets-input" value="${this.filters.p_yield_brackets}" placeholder="e.g. 2,4" data-testing-id="yield-brackets-input" />
                        </div>
                       </div>
                        
                    </div>
                    <div class="chartDv" id="topIndexContainer"></div>
                    <div class="legendRowDYHeathmap">
                        <p>Intensity</p>
                        <div class="leg">
                            <span></span>
                            <span></span>
                            <p>Low > High</p>
                        </div>
                    </div>
                </div>
            </div>
        `;
        this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

        const yieldInput = this.shadowRoot.querySelector('.yield-brackets-input') as HTMLInputElement;
        if (yieldInput) {
            yieldInput.addEventListener('change', (e) => {
                this.filters.p_yield_brackets = yieldInput.value;
                this.setupChart();
            });
        }

        const filtersToggleBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement | null;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
        if (filtersToggleBtn && filtersBox) {
            filtersToggleBtn.addEventListener('click', () => {
                filtersBox.classList.toggle('hidden');
            });
        }

        const freqSelect = this.shadowRoot.querySelector('.frequency-select') as HTMLSelectElement;
        if (freqSelect) {
            freqSelect.addEventListener('change', (e) => {
                this.filters.p_frequency = freqSelect.value;
                this.setupChart();
            });
        }

        const input = this.shadowRoot.querySelector('.date-input') as HTMLInputElement;
        let defaultDates: string[] | undefined = undefined;
        if (this.filters.p_start_date && this.filters.p_end_date) {
            defaultDates = [this.filters.p_start_date, this.filters.p_end_date];
            input.value = `${this.filters.p_start_date} to ${this.filters.p_end_date}`;
        } else {
            const today = new Date();
            const endDate = today.toISOString().slice(0, 10);
            const startDateObj = new Date(today);
            startDateObj.setMonth(today.getMonth() - 1);
            if (startDateObj.getMonth() === today.getMonth()) {
                startDateObj.setDate(0);
            }
            const startDate = startDateObj.toISOString().slice(0, 10);
            defaultDates = [startDate, endDate];
            input.value = `${startDate} to ${endDate}`;
            this.filters.p_start_date = startDate;
            this.filters.p_end_date = endDate;
        }
        initFlatpickrInShadow(input, this.shadowRoot, {
            mode: 'range',
            dateFormat: 'Y-m-d',
            defaultDate: defaultDates,
            onClose: (selectedDates: Date[]) => {
                if (selectedDates.length === 2) {
                    const [start, end] = selectedDates;
                    const formatLocalDate = (date: Date) => {
                        const year = date.getFullYear();
                        const month = (date.getMonth() + 1).toString().padStart(2, '0');
                        const day = date.getDate().toString().padStart(2, '0');
                        return `${year}-${month}-${day}`;
                    };
                    this.filters.p_start_date = formatLocalDate(start);
                    this.filters.p_end_date = formatLocalDate(end);
                    input.value = `${this.filters.p_start_date} to ${this.filters.p_end_date}`;
                    this.setupChart();
                }
            },
        });
    }

    private async attachStyles(): Promise<void> {
        try {
            appendFlatpickrBaseStyles(this.shadowRoot);
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }


    private async setupChart(): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        try {
            // Build query string from filters object
            const params = new URLSearchParams({
                p_yield_brackets: this.filters.p_yield_brackets,
                p_frequency: this.filters.p_frequency,
                p_start_date: this.filters.p_start_date,
                p_end_date: this.filters.p_end_date,
            });
            const url = `${APIURL}/v1/discover/dividend-yield-heatmap?${params.toString()}`;
            const response = await apiGet({
                url,
                method: 'GET',
            });

            if (!response.ok) throw new Error("Failed to fetch data");
            const data = await response.json();
            const items = Array.isArray(data) ? data : (data && (data.data || data.items || data.results)) || [];
            this.currentData = items;
            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error("Error fetching chart data:", err);
            this.currentData = [];
            this.updateDisplay();
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (!this.currentData) {
            return;
        }
        if (this.currentData.length === 0) {
            widgetBox?.removeAttribute('data-testing-id');
        }
        this.renderAnalystRating();
    }

    private renderAnalystRating(): void {
        const container = this.shadowRoot.querySelector('#topIndexContainer');
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (!container) return;

        const items = Array.isArray(this.currentData)
            ? this.currentData
            : (this.currentData && (this.currentData.data || this.currentData.items)) || [];

        if (!Array.isArray(items) || items.length === 0) {
            widgetBox?.removeAttribute('data-testing-id');
            container.innerHTML = getNoDataHTML();
            return;
        }

        const itemHtml = items.map((item: any, idx: number) => `
            <div class="item" style="--low:10%; --high:90%" data-testing-id="dividend-yield-item-${idx}" data-yield-range="${item.yield_range?.replace(/[^a-zA-Z0-9]/g, '_') || ''}" data-stock-count="${item.stock_count || 0}">
                <p>${item.yield_range}</p>
                <h5>${item.stock_count}</h5>
                <p>Stocks</p>
            </div>
        `).join('');

        container.innerHTML = `
            <div class="chartDv">
                <div class="divYeildHeatmapMain">
                    ${itemHtml}
                </div>
            </div>
        `;
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        if (!this.selectedSymbols.includes(stockId)) {
            this.selectedSymbols.push(stockId);
            this.setupChart();
        }
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(), (type: any, data: any) => this.emit(type, data));
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;
    
        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
        this.updateDisplay();
    }
    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseMarketDividendYieldHeatmap: typeof TraderverseMarketDividendYieldHeatmap; } }
if (typeof window !== 'undefined') { window.TraderverseMarketDividendYieldHeatmap = TraderverseMarketDividendYieldHeatmap; }